import React, { useState } from 'react';
import { Baby, Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface MaternityState {
  averageSalary: string;
  monthsWorked: string;
  country: 'HR' | 'BA' | 'RS';
  employmentType: 'employed' | 'self-employed' | 'unemployed';
  childOrder: '1' | '2' | '3+';
}

const countryConfigs = {
  HR: {
    currency: 'EUR',
    symbol: '€',
    minBenefit: 700,
    maxBenefit: 2500,
    percentageOfSalary: 1, // 100% of salary
    minMonthsRequired: 12,
    unemployedBenefit: 300
  },
  BA: {
    currency: 'BAM',
    symbol: 'KM',
    minBenefit: 400,
    maxBenefit: 2000,
    percentageOfSalary: 0.8, // 80% of salary
    minMonthsRequired: 12,
    unemployedBenefit: 200
  },
  RS: {
    currency: 'RSD',
    symbol: 'RSD',
    minBenefit: 35000,
    maxBenefit: 160000,
    percentageOfSalary: 1, // 100% of salary
    minMonthsRequired: 6,
    unemployedBenefit: 20000
  }
};

function KalkulatorPorodiljneNaknade() {
  const [values, setValues] = useState<MaternityState>({
    averageSalary: '',
    monthsWorked: '',
    country: 'HR',
    employmentType: 'employed',
    childOrder: '1'
  });

  const [results, setResults] = useState<{
    monthlyBenefit: number;
    totalBenefit: number;
    duration: number;
    isEligible: boolean;
    message: string;
  } | null>(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Porodiljne Naknade",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte porodiljnu naknadu za Hrvatsku, BiH i Srbiju. Precizni izračuni na temelju plaće i staža.",
    "url": "https://online-kalkulator.com/kalkulator-porodiljne-naknade",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun porodiljne naknade",
      "Podrška za više država",
      "Izračun trajanja naknade",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  const calculateBenefit = () => {
    const config = countryConfigs[values.country];
    const salary = parseFloat(values.averageSalary);
    const months = parseFloat(values.monthsWorked);
    
    if (isNaN(salary) || isNaN(months)) {
      return null;
    }

    let duration = 12; // Base duration in months
    if (values.childOrder === '2') duration = 15;
    if (values.childOrder === '3+') duration = 18;

    let monthlyBenefit = 0;
    let isEligible = true;
    let message = '';

    if (values.employmentType === 'unemployed') {
      monthlyBenefit = config.unemployedBenefit;
      message = 'Naknada za nezaposlene majke';
    } else {
      if (months < config.minMonthsRequired) {
        isEligible = false;
        message = `Potrebno je minimalno ${config.minMonthsRequired} mjeseci staža za punu naknadu`;
        monthlyBenefit = config.minBenefit;
      } else {
        monthlyBenefit = Math.min(
          Math.max(salary * config.percentageOfSalary, config.minBenefit),
          config.maxBenefit
        );
        message = 'Puna naknada na temelju plaće';
      }
    }

    const totalBenefit = monthlyBenefit * duration;

    return {
      monthlyBenefit: parseFloat(monthlyBenefit.toFixed(2)),
      totalBenefit: parseFloat(totalBenefit.toFixed(2)),
      duration,
      isEligible,
      message
    };
  };

  const handleInputChange = (field: keyof MaternityState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCalculate = () => {
    const results = calculateBenefit();
    setResults(results);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <>
      <SEO 
        title="Kalkulator Porodiljne Naknade | Izračun Naknade za Vrijeme Porodiljnog"
        description="Izračunajte porodiljnu naknadu za Hrvatsku, BiH i Srbiju. Precizni izračuni na temelju plaće i staža. Informativni kalkulator za trudnice."
        canonicalUrl="/kalkulator-porodiljne-naknade"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Baby className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Porodiljne Naknade</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prosječna mjesečna plaća
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.averageSalary}
                      onChange={(e) => handleInputChange('averageSalary', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${countryConfigs[values.country].currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mjeseci radnog staža
                  </label>
                  <input
                    type="number"
                    value={values.monthsWorked}
                    onChange={(e) => handleInputChange('monthsWorked', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Država
                  </label>
                  <select
                    value={values.country}
                    onChange={(e) => handleInputChange('country', e.target.value as MaternityState['country'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="HR">Hrvatska</option>
                    <option value="BA">Bosna i Hercegovina</option>
                    <option value="RS">Srbija</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status zaposlenja
                  </label>
                  <select
                    value={values.employmentType}
                    onChange={(e) => handleInputChange('employmentType', e.target.value as MaternityState['employmentType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="employed">Zaposlena</option>
                    <option value="self-employed">Samostalna djelatnost</option>
                    <option value="unemployed">Nezaposlena</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Koje dijete po redu
                  </label>
                  <select
                    value={values.childOrder}
                    onChange={(e) => handleInputChange('childOrder', e.target.value as MaternityState['childOrder'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="1">Prvo dijete</option>
                    <option value="2">Drugo dijete</option>
                    <option value="3+">Treće ili više</option>
                  </select>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className={`text-lg ${results.isEligible ? 'text-green-600' : 'text-blue-600'}`}>
                      {results.message}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Mjesečna naknada:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.monthlyBenefit)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Trajanje naknade:</span>
                    <span className="font-semibold">
                      {results.duration} mjeseci
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupna naknada:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.totalBenefit)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Porodiljne Naknade</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator porodiljne naknade pomaže vam izračunati očekivani iznos naknade tijekom
                  porodiljnog dopusta. Uzima u obzir različite faktore kao što su prosječna plaća,
                  duljina radnog staža, broj djece i status zaposlenja.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Uvjeti za Naknadu</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Hrvatska</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 12 mjeseci neprekidnog staža</li>
                      <li>100% prosječne plaće za zaposlene</li>
                      <li>Minimalna naknada: 700 EUR</li>
                      <li>Maksimalna naknada: 2500 EUR</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Bosna i Hercegovina</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 12 mjeseci staža</li>
                      <li>80% prosječne plaće</li>
                      <li>Minimalna naknada: 400 BAM</li>
                      <li>Maksimalna naknada: 2000 BAM</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Srbija</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Minimalno 6 mjeseci staža</li>
                      <li>100% prosječne plaće</li>
                      <li>Minimalna naknada: 35.000 RSD</li>
                      <li>Maksimalna naknada: 160.000 RSD</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Trajanje Porodiljnog Dopusta</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Za prvo dijete: 12 mjeseci</li>
                  <li>Za drugo dijete: 15 mjeseci</li>
                  <li>Za treće i svako sljedeće dijete: 18 mjeseci</li>
                  <li>Za blizance: dodatnih 3 mjeseca</li>
                  <li>Za prijevremeni porod: do navršenih 12 mjeseci djeteta</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite prosječnu mjesečnu plaću</li>
                  <li>Unesite broj mjeseci radnog staža</li>
                  <li>Odaberite državu</li>
                  <li>Odaberite status zaposlenja</li>
                  <li>Označite koje je dijete po redu</li>
                  <li>Kliknite "Izračunaj" za rezultate</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je informativne prirode</li>
                  <li>Stvarni iznos može odstupati od izračuna</li>
                  <li>Konzultirajte se s nadležnim institucijama</li>
                  <li>Provjerite aktualne zakone i propise</li>
                  <li>Posebni slučajevi mogu imati drugačije uvjete</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dodatne Informacije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Pravo na naknadu imaju i posvojitelji</li>
                  <li>Očevi mogu koristiti roditeljski dopust</li>
                  <li>Moguće je raditi pola radnog vremena</li>
                  <li>Produženje u slučaju komplikacija</li>
                  <li>Posebna pravila za samohrane roditelje</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorPorodiljneNaknade;